/** 全书章节使用单组批量选择；加入/移出投放保存引用，AI 操作复用共享章节结果。 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AdminApiError } from '~/api/errors';
import {
  type ProjectChapter,
  type ProjectDetail,
  type ProjectMutation,
  landingProjectQueryKey,
  projectExecutionActive,
  useLandingChapterHistory,
  useLandingChapterHistoryDetail,
  useLandingProjectChapterCatalog,
  useLandingProjectChapters,
} from '~/data/queries';
import { landingProjectsApi } from '~/hooks/use-landing-projects';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { landingAiProviderLabel } from '~/lib/landing-ai-provider-label';
import { useAuthStore } from '~/stores/auth-store';
import { ContextMenu } from '~/ui/ContextMenu';
import { ConfirmDialog, Drawer } from '~/ui/Dialog';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { StatusChip } from '~/ui/StatusChip';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { LandingTextDiff } from '../dialogs/LandingDesensitizationLogDialog';
import { LandingChapterManualContentDialog } from './LandingChapterManualContentDialog';
import { LandingChapterReviewDrawer } from './LandingChapterReviewDrawer';
import { LandingCollapsibleNote } from './landing-collapsible-note';
import { ProjectState, projectPhaseLabel, toggleSelection } from './landing-project-display';
export function LandingProjectChaptersPanel({
  project,
  onChanged,
  onExecution,
}: {
  project: ProjectDetail;
  onChanged: (result: ProjectMutation) => void;
  onExecution: (id: string) => void;
}) {
  const role = useAuthStore((state) => state.user?.role);
  const hasProcessingRole = role === 'root' || role === 'admin' || role === 'operator';
  const canProcess = hasProcessingRole && project.can_generate;
  const query = useLandingProjectChapters(project.id);
  const catalog = useLandingProjectChapterCatalog(project.id);
  const client = useQueryClient();
  const [selected, setSelected] = useState(new Set<string>());
  const [search, setSearch] = useState('');
  const [freePage, setFreePage] = useState(1);
  const [paidPage, setPaidPage] = useState(1);
  const [removing, setRemoving] = useState(false);
  const [replacingUnavailable, setReplacingUnavailable] = useState(false);
  const [historyOf, setHistoryOf] = useState<{
    chapterId: string;
    type?: 'RULE' | 'AI';
    versionId?: string | null;
  } | null>(null);
  const [reviewOf, setReviewOf] = useState<{
    chapterId: string;
  } | null>(null);
  const [editingContent, setEditingContent] = useState<ProjectChapter | null>(null);
  const [force, setForce] = useState(false);
  const [process, setProcess] = useState<'review' | 'desensitize' | null>(null);
  const change = useMutation({
    mutationFn: (ids: string[]) => landingProjectsApi.replaceChapters(project.id, ids),
    onSuccess: (result) => {
      setRemoving(false);
      setReplacingUnavailable(false);
      void client.invalidateQueries({ queryKey: [...landingProjectQueryKey, project.id] });
      onChanged(result);
    },
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  const submission = useMutation({
    mutationFn: (input: {
      operation: 'review' | 'desensitize';
      chapter_ids: string[];
      force?: boolean;
    }) => {
      if (!project.can_generate)
        throw new Error(project.generation_blocked_reason ?? '当前书籍不能投放');
      return landingProjectsApi.process(project.id, input);
    },
    onSuccess: (execution) => {
      setProcess(null);
      void client.invalidateQueries({ queryKey: landingProjectQueryKey });
      onExecution(execution.id);
      toast('已提交后台处理，关闭详情不影响执行', { tone: 'info' });
    },
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  const savedIds = new Set(project.chapter_ids);
  const adopted = new Map((query.data ?? []).map((row) => [row.id, row]));
  const availableIds = new Set((catalog.data ?? []).map((row) => row.id));
  const rows = [
    ...(catalog.data ?? []).map((row) => adopted.get(row.id) ?? row),
    ...(query.data ?? []).filter((row) => !availableIds.has(row.id)),
  ].sort((a, b) => a.chapter_index - b.chapter_index);
  const currentSelection = rows.filter((row) => selected.has(row.id));
  const canSelect = (row: ProjectChapter) => row.selectable || savedIds.has(row.id);
  const chaptersToAdd = currentSelection.filter((row) => row.selectable && !savedIds.has(row.id));
  const chaptersToRemove = currentSelection.filter((row) => savedIds.has(row.id));
  const unavailableSavedIds = new Set(
    rows.filter((row) => savedIds.has(row.id) && !row.selectable).map((row) => row.id),
  );
  const addedIds = new Set(chaptersToAdd.map((row) => row.id));
  const joiningIds = [
    ...rows
      .filter(
        (row) => (savedIds.has(row.id) && !unavailableSavedIds.has(row.id)) || addedIds.has(row.id),
      )
      .map((row) => row.id),
    ...project.chapter_ids.filter((id) => !rows.some((row) => row.id === id)),
  ];
  const selectedIds = new Set(currentSelection.map((row) => row.id));
  const loading = query.isPending || catalog.isPending || query.isError || catalog.isError;
  const actionPending = change.isPending || submission.isPending;
  const canRunAi =
    canProcess &&
    !loading &&
    !actionPending &&
    currentSelection.length > 0 &&
    currentSelection.every((row) => row.selectable && savedIds.has(row.id));
  const removingIds = project.chapter_ids.filter((id) => !selectedIds.has(id));
  const remainingUnavailableCount = removingIds.filter((id) => unavailableSavedIds.has(id)).length;
  const removalBlockedReason = !chaptersToRemove.length
    ? undefined
    : !removingIds.length
      ? '项目至少保留一个投放章节'
      : remainingUnavailableCount
        ? `请同时勾选移出剩余的 ${remainingUnavailableCount} 个失效投放章节`
        : undefined;
  const canRemove =
    hasProcessingRole &&
    !loading &&
    !actionPending &&
    chaptersToRemove.length > 0 &&
    !removalBlockedReason;
  const openReview = (row: ProjectChapter) => {
    const status = row.review_status;
    if (row.execution && ['PENDING', 'RUNNING'].includes(status)) onExecution(row.execution.id);
    else setReviewOf({ chapterId: row.id });
  };
  const openDesensitization = (row: ProjectChapter) => {
    if (row.execution && ['PENDING', 'RUNNING'].includes(row.desensitization_status))
      onExecution(row.execution.id);
    else
      setHistoryOf({
        chapterId: row.id,
        type: 'AI',
        versionId: row.desensitization_content_version_id,
      });
  };
  const filteredRows = rows.filter(
    (row) =>
      !search ||
      String(row.chapter_index) === search.trim() ||
      row.title.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  const eligibleRows = filteredRows.filter((row) => row.selectable);
  const freeRows = filteredRows.filter((row) => row.is_free);
  const paidRows = filteredRows.filter((row) => !row.is_free);
  const renderChapterTable = (
    tableRows: typeof rows,
    accessLabel: string,
    page: number,
    setPage: (page: number) => void,
  ) => (
    <>
      <Table
        className="landing-content-table landing-chapter-table"
        style={{ minWidth: 1120, tableLayout: 'fixed' }}
      >
        <colgroup>
          {[56, 340, 80, 100, 120, 120, 160, 170].map((width, index) => (
            <col key={index} style={{ width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                aria-label={`全选${accessLabel}章节`}
                disabled={actionPending}
                ref={(element) => {
                  if (element) {
                    const eligible = tableRows.filter(canSelect);
                    element.indeterminate =
                      eligible.some((row) => selected.has(row.id)) &&
                      !eligible.every((row) => selected.has(row.id));
                  }
                }}
                checked={
                  tableRows.some(canSelect) &&
                  tableRows.filter(canSelect).every((row) => selected.has(row.id))
                }
                onChange={(event) =>
                  setSelected((current) => {
                    const next = new Set(current);
                    for (const row of tableRows.filter(canSelect))
                      if (event.target.checked) next.add(row.id);
                      else next.delete(row.id);
                    return next;
                  })
                }
              />
            </th>
            <th>章号 / 标题</th>
            <th>源版本</th>
            <th>词库规则</th>
            <th>AI 审核</th>
            <th>AI 脱敏</th>
            <th>进度 / 原因</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {tableRows
            .slice(
              (Math.min(page, Math.max(1, Math.ceil(tableRows.length / 50))) - 1) * 50,
              Math.min(page, Math.max(1, Math.ceil(tableRows.length / 50))) * 50,
            )
            .map((row) => (
              <tr key={row.id}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`选择第 ${row.chapter_index} 章`}
                    checked={selected.has(row.id)}
                    disabled={!canSelect(row) || actionPending}
                    onChange={() => setSelected((current) => toggleSelection(current, row.id))}
                  />
                </td>
                <td>
                  <span className="mono">{row.chapter_index}</span> · {row.title}{' '}
                  <span className={`chip ${row.is_free ? 'chip-pos' : 'chip-warn'}`}>
                    {row.is_free ? '免费' : '付费'}
                  </span>
                  {row.reused && <div className="chip chip-info">共享结果复用</div>}
                  {savedIds.has(row.id) ? (
                    <StatusChip label="已加入投放" tone="info" />
                  ) : (
                    <span className="muted"> · 未加入投放</span>
                  )}
                  {!row.selectable && <div className="muted">{row.unavailable_reason}</div>}
                </td>
                <td className="mono">
                  v{row.source_version}
                  {row.is_source_stale ? (
                    <div className="chip chip-warn">原文已变化</div>
                  ) : (
                    row.is_recipe_stale && (
                      <div
                        className="chip chip-info"
                        title="审核 / 脱敏的模型、提示词或词库已更新，原文未变；需按新方案重新处理"
                      >
                        方案已更新
                      </div>
                    )
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    title={`查看第 ${row.chapter_index} 章词库规则详情`}
                    disabled={!savedIds.has(row.id)}
                    onClick={() => setHistoryOf({ chapterId: row.id, type: 'RULE' })}
                  >
                    <ProjectState status={row.rule_status} />
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    title={`查看第 ${row.chapter_index} 章 AI 审核详情`}
                    disabled={!savedIds.has(row.id)}
                    onClick={() => openReview(row)}
                  >
                    <ProjectState
                      status={row.review_status}
                      label={row.review_status === 'STALE' ? '待重新审核' : undefined}
                    />
                  </button>
                  {row.review_result && (
                    <div className="muted">
                      词 {row.review_result.illegal_words.length} · 句{' '}
                      {row.review_result.illegal_phrases.length}
                    </div>
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    title={`查看第 ${row.chapter_index} 章 AI 脱敏详情`}
                    disabled={!savedIds.has(row.id)}
                    onClick={() => openDesensitization(row)}
                  >
                    <ProjectState
                      status={row.desensitization_status}
                      label={row.desensitization_status === 'STALE' ? '待重新脱敏' : undefined}
                    />
                  </button>
                  {/* 原因常列出多项违规词句，默认折叠成一行结论，点「详情」看全文。 */}
                  {row.adoption_block_reason ? (
                    <LandingCollapsibleNote
                      text={row.adoption_block_reason}
                      label={`第 ${row.chapter_index} 章未采用原因`}
                    />
                  ) : row.desensitization_status === 'SUCCEEDED' &&
                    row.desensitization_result_adopted === false ? (
                    <div className="muted">脱敏已完成，结果尚未采用</div>
                  ) : row.desensitization_status === 'SUCCEEDED' && row.adoption_hint ? (
                    // 成功即采用（v3）后的抽查提示，只在 AI 脱敏「成功」下展示，不阻断 HTML 生成。
                    <LandingCollapsibleNote
                      text={row.adoption_hint}
                      label={`第 ${row.chapter_index} 章抽查提示`}
                    />
                  ) : null}
                </td>
                <td>
                  {row.execution && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={{ whiteSpace: 'normal', height: 'auto', minHeight: 'var(--btn-h)' }}
                      onClick={() => onExecution(row.execution?.id ?? '')}
                    >
                      {projectPhaseLabel(row.execution.phase)} · {row.execution.completed}/
                      {row.execution.total}
                    </button>
                  )}
                  {row.error && (
                    // 章节失败原因只来自终态失败；章节已有新执行在跑时弱化，不再按报错强调。
                    // 失败原因在前段，摘要取原文一行截断，不按「结论在末段」去取。
                    <LandingCollapsibleNote
                      text={row.error}
                      summary={row.error}
                      tone={projectExecutionActive(row.execution) ? 'muted' : 'neg'}
                      label={`第 ${row.chapter_index} 章失败原因`}
                    />
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={!savedIds.has(row.id)}
                    onClick={() => setHistoryOf({ chapterId: row.id })}
                  >
                    日志 / Diff
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={
                      !canProcess ||
                      !savedIds.has(row.id) ||
                      (!!row.execution && ['PENDING', 'RUNNING'].includes(row.execution.status))
                    }
                    onClick={() => setEditingContent(row)}
                  >
                    人工修正
                  </button>
                </td>
              </tr>
            ))}
          {!tableRows.length && (
            <tr>
              <td colSpan={8}>
                {search ? '当前筛选没有' : '本书没有可用'}
                {accessLabel}章节
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {tableRows.length > 50 && (
        <Pagination total={tableRows.length} page={page} pageSize={50} onChange={setPage} />
      )}
    </>
  );
  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn-accent"
          disabled={
            !canProcess ||
            loading ||
            actionPending ||
            !chaptersToAdd.length ||
            joiningIds.length > 1000
          }
          onClick={() => {
            if (unavailableSavedIds.size) setReplacingUnavailable(true);
            else change.mutate(joiningIds);
          }}
        >
          {change.isPending && !removing ? '加入中…' : '加入投放'}
        </button>
        <button
          type="button"
          className="btn"
          disabled={!canRunAi}
          onClick={() => {
            setForce(false);
            setProcess('review');
          }}
        >
          AI 审核
        </button>
        <button
          type="button"
          className="btn"
          disabled={!canRunAi}
          onClick={() => {
            setForce(false);
            setProcess('desensitize');
          }}
        >
          AI 脱敏
        </button>
        <ContextMenu
          items={[
            {
              label: '移出投放',
              disabled: !canRemove,
              title: removalBlockedReason,
              onClick: () => setRemoving(true),
            },
            '---',
            {
              label: '强制重新审核所选章节',
              disabled: !canRunAi,
              onClick: () => {
                setForce(true);
                setProcess('review');
              },
            },
            {
              label: '强制重新脱敏所选章节',
              disabled: !canRunAi,
              onClick: () => {
                setForce(true);
                setProcess('desensitize');
              },
            },
          ]}
        >
          <button
            type="button"
            className="btn"
            disabled={!hasProcessingRole || !currentSelection.length || actionPending || loading}
          >
            更多操作
          </button>
        </ContextMenu>
        <span className="muted">已选 {currentSelection.length} 章</span>
        {currentSelection.length > 0 && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={actionPending}
            onClick={() => setSelected(new Set())}
          >
            清空选择
          </button>
        )}
        {!hasProcessingRole && <span className="muted">AI 处理需要运营员及以上权限</span>}
        {!project.can_generate && (
          <span role="alert" className="muted">
            {project.generation_blocked_reason ?? '当前书籍不能投放'}
          </span>
        )}
        <span className="muted">
          审核使用小说原文，脱敏完成后自动执行词库替换；使用最新模型与提示词
        </span>
      </div>
      <SearchInput
        value={search}
        placeholder="搜索全书章号 / 标题"
        onChange={(event) => {
          setSearch(event.target.value);
          setFreePage(1);
          setPaidPage(1);
        }}
        onClear={() => {
          setSearch('');
          setFreePage(1);
          setPaidPage(1);
        }}
      />
      <p className="muted">
        全书已发布章节 {rows.filter((row) => row.selectable).length} 章 · 已加入投放{' '}
        {project.chapter_ids.length} 章。勾选章节后选择 批量操作，加入或移出投放后需要重新生成 HTML
        并重新下载。
      </p>
      {unavailableSavedIds.size > 0 && (
        <p className="muted">
          另有 {unavailableSavedIds.size} 个失效投放章节保留在列表中，可勾选移出。
        </p>
      )}
      {remainingUnavailableCount > 0 && chaptersToRemove.length > 0 && (
        <p role="alert" className="muted">
          {removalBlockedReason}，再确认移出。
        </p>
      )}
      {chaptersToAdd.length > 0 && (
        <p role="alert" className="muted">
          已选章节中有 {chaptersToAdd.length} 章尚未加入投放，请先加入投放再执行 AI 处理。
        </p>
      )}
      {joiningIds.length > 1000 && <p role="alert">每个项目最多加入 1000 章，请减少本次选择。</p>}
      {query.isError || catalog.isError ? (
        <p role="alert">
          {(query.error ?? catalog.error)?.message}
          <button
            type="button"
            className="btn"
            onClick={() => {
              void query.refetch();
              void catalog.refetch();
            }}
          >
            重试加载
          </button>
        </p>
      ) : query.isPending || catalog.isPending ? (
        <p>加载章节处理状态…</p>
      ) : (
        <>
          <div className="muted">
            免费章节（{eligibleRows.filter((row) => row.is_free).length}）
          </div>
          {renderChapterTable(freeRows, '免费', freePage, setFreePage)}
          <details className="card-flat landing-chapter-collapse">
            <summary>
              <Icons.IconChevRight size={12} className="landing-collapse-chevron" />
              付费章节（{eligibleRows.filter((row) => !row.is_free).length} 章，投放已选{' '}
              {eligibleRows.filter((row) => !row.is_free && savedIds.has(row.id)).length}）
            </summary>
            <div className="landing-collapse-content">
              {renderChapterTable(paidRows, '付费', paidPage, setPaidPage)}
            </div>
          </details>
        </>
      )}
      {replacingUnavailable && (
        <ConfirmDialog
          title="加入章节并移出失效章节？"
          message={`原投放范围中有 ${unavailableSavedIds.size} 章已删除或未发布。加入所选 ${chaptersToAdd.length} 章时，将同时移出这些失效章节。`}
          details={['公共处理记录保留；需要重新生成 HTML 并重新下载。']}
          confirmLabel="确认加入投放"
          onConfirm={() => {
            if (
              canProcess &&
              !loading &&
              !actionPending &&
              chaptersToAdd.length &&
              joiningIds.length <= 1000
            )
              change.mutate(joiningIds);
          }}
          onClose={() => setReplacingUnavailable(false)}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="移出投放章节？"
          message={`将已选的 ${chaptersToRemove.length} 个投放章节移出本项目，公共处理记录保留。`}
          details={['范围改变后，需要重新生成 HTML 并重新下载。']}
          confirmLabel="移出投放"
          onConfirm={() => {
            if (canRemove) {
              change.mutate(removingIds);
            }
          }}
          onClose={() => setRemoving(false)}
        />
      )}
      {process && (
        <ConfirmDialog
          title={process === 'review' ? '批量 AI 审核' : '批量 AI 脱敏'}
          message={
            force
              ? `强制重新处理已选 ${currentSelection.length} 章，会产生新的模型调用和版本记录，不复用已有结果。`
              : `已选 ${currentSelection.length} 章，将使用最新配置核对已有结果，仅处理缺少有效结果的章节。`
          }
          details={[
            '提交时自动使用最新审核/脱敏模型、提示词及书籍对应语言词库。',
            '只修改营销正文副本，保留原书。',
            '处理在后台持续执行，结果和失败原因可随时查看。',
          ]}
          confirmLabel="提交后台处理"
          onConfirm={() => {
            if (canRunAi) {
              submission.mutate({
                operation: process,
                chapter_ids: currentSelection.map((row) => row.id),
                ...(force ? { force: true } : {}),
              });
            }
          }}
          onClose={() => setProcess(null)}
        />
      )}
      {historyOf && (
        <LandingChapterHistoryDrawer
          project={project}
          key={`${historyOf.chapterId}-${historyOf.type ?? 'all'}`}
          chapterId={historyOf.chapterId}
          processingType={historyOf.type}
          preferredVersionId={historyOf.versionId}
          chapter={rows.find((row) => row.id === historyOf.chapterId)}
          onChanged={onChanged}
          onClose={() => setHistoryOf(null)}
        />
      )}
      {reviewOf && rows.find((row) => row.id === reviewOf.chapterId) && (
        <LandingChapterReviewDrawer
          chapter={rows.find((row) => row.id === reviewOf.chapterId) as ProjectChapter}
          onClose={() => setReviewOf(null)}
        />
      )}
      {editingContent && (
        <LandingChapterManualContentDialog
          projectId={project.id}
          chapter={editingContent}
          onChanged={onChanged}
          onClose={() => setEditingContent(null)}
        />
      )}
    </div>
  );
}
function LandingChapterHistoryDrawer({
  project,
  chapterId,
  processingType,
  preferredVersionId,
  chapter,
  onClose,
  onChanged,
}: {
  project: ProjectDetail;
  chapterId: string;
  processingType?: 'RULE' | 'AI';
  preferredVersionId?: string | null;
  chapter?: ProjectChapter;
  onClose: () => void;
  onChanged: (result: ProjectMutation) => void;
}) {
  const [page, setPage] = useState(1);
  const [chosen, setChosen] = useState<string | null>(null);
  const [mode, setMode] = useState<'split' | 'unified'>('split');
  const [confirmAdoption, setConfirmAdoption] = useState<{
    versionId: string;
    reviewed: boolean;
  } | null>(null);
  const [confirmRejection, setConfirmRejection] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState(
    '内容未符合脱敏要求：剧情原意或高危描写弱化效果不合格',
  );
  const role = useAuthStore((state) => state.user?.role);
  const canAdopt = project.can_generate && ['root', 'admin', 'operator'].includes(role ?? '');
  const canReject = ['root', 'admin', 'operator'].includes(role ?? '');
  const history = useLandingChapterHistory(project.id, chapterId, page);
  const records = (history.data?.data ?? []).filter(
    (log) =>
      !processingType || log.processing_type === processingType || log.id === preferredVersionId,
  );
  const id = chosen ?? (page === 1 ? preferredVersionId : null) ?? records[0]?.id ?? null;
  const detail = useLandingChapterHistoryDetail(project.id, chapterId, id);
  const [adoptionFailure, setAdoptionFailure] = useState<{
    versionId: string;
    reason: string;
  } | null>(null);
  const adoptionError = adoptionFailure?.versionId === id ? adoptionFailure.reason : null;
  const adopt = useMutation({
    mutationFn: (selection: { versionId: string; reviewed: boolean }) =>
      landingProjectsApi.adopt(project.id, chapterId, selection.versionId, selection.reviewed),
    onSuccess: (result) => onChanged(result),
    onError: (error: Error, selection) => {
      const reason =
        error instanceof AdminApiError && error.code === 'RESOURCE_CONFLICT'
          ? typeof error.details?.adopt_block_reason === 'string'
            ? error.details.adopt_block_reason
            : '该版本当前不可采用，请查看版本资格及原因'
          : error.message;
      setAdoptionFailure({ versionId: selection.versionId, reason });
      setConfirmAdoption(null);
      void detail.refetch();
      void history.refetch();
      toast(reason, { tone: 'neg' });
    },
  });
  const reject = useMutation({
    mutationFn: (versionId: string) =>
      landingProjectsApi.reject(project.id, chapterId, versionId, rejectionReason.trim()),
    onSuccess: (result) => onChanged(result),
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  return (
    <Drawer
      title={
        processingType === 'RULE'
          ? '规则处理日志与 Diff'
          : processingType === 'AI'
            ? 'AI 脱敏日志与 Diff'
            : '章节处理日志与 Diff'
      }
      subtitle={
        processingType
          ? '仅展示对应正文处理版本；不修改原书'
          : '规则、AI 脱敏及人工正文版本；审核进度和结果请点击审核状态'
      }
      width={1060}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            关闭
          </button>
          {canAdopt &&
            id &&
            !detail.data?.log.is_current &&
            detail.data?.log.status === 'SUCCEEDED' &&
            !detail.data.log.is_rejected && (
              <button
                type="button"
                className="btn btn-accent"
                disabled={adopt.isPending || detail.data?.log.can_adopt !== true}
                onClick={() =>
                  setConfirmAdoption({
                    versionId: id,
                    reviewed: typeof detail.data?.log.adoption_block_reason === 'string',
                  })
                }
              >
                采用此版本
              </button>
            )}
          {canReject &&
            id &&
            detail.data?.log.is_current &&
            detail.data.log.is_rejected === false && (
              <button
                type="button"
                className="btn btn-danger"
                disabled={reject.isPending}
                onClick={() => setConfirmRejection(id)}
              >
                拒用并撤销采用
              </button>
            )}
        </>
      }
    >
      <div className="vstack" style={{ padding: 18, gap: 12 }}>
        {(adoptionError ||
          detail.data?.log.adopt_block_reason ||
          detail.data?.log.adoption_block_reason ||
          (detail.data && detail.data.log.can_adopt === undefined)) && (
          <p role="alert" className="muted">
            {adoptionError ||
              detail.data?.log.adopt_block_reason ||
              detail.data?.log.adoption_block_reason ||
              '当前服务尚未返回采用资格，暂不可采用'}
          </p>
        )}
        {confirmAdoption && (
          <ConfirmDialog
            title="确认内容验收并采用"
            message="请确认已核对剧情与动作本意，且高危描写确实弱化。采用后将替换当前营销正文，原书不变。"
            confirmLabel="已验收，采用此版本"
            onConfirm={() => adopt.mutate(confirmAdoption)}
            onClose={() => setConfirmAdoption(null)}
          />
        )}
        {confirmRejection && (
          <ConfirmDialog
            title="拒用并撤销当前采用"
            tone="danger"
            message={
              <div className="vstack">
                <p>撤销本章当前采用，保留全部版本和已完成结果；相同正文不能再次采用。</p>
                <label>
                  拒用原因
                  <textarea
                    className="input"
                    value={rejectionReason}
                    maxLength={1000}
                    onChange={(event) => setRejectionReason(event.target.value)}
                  />
                </label>
              </div>
            }
            confirmLabel="拒用并撤销采用"
            onConfirm={() => {
              if (rejectionReason.trim()) reject.mutate(confirmRejection);
              else toast('请填写拒用原因', { tone: 'neg' });
            }}
            onClose={() => setConfirmRejection(null)}
          />
        )}
        {chapter && (
          <p className="mono">
            第 {chapter.chapter_index} 章 · {chapter.title}
          </p>
        )}
        {processingType === 'AI' && chapter?.desensitization_status === 'FAILED' && (
          <p role="alert" style={{ color: 'var(--neg)' }}>
            本次 AI 脱敏执行失败：{chapter.error ?? '请查看执行记录'}。下方正文版本仅供历史参考。
          </p>
        )}
        {history.isPending ? (
          <p>加载历史…</p>
        ) : history.isError ? (
          <div role="alert" className="card-flat" style={{ padding: 12 }}>
            <p>{history.error.message}</p>
            <button type="button" className="btn btn-sm" onClick={() => void history.refetch()}>
              重新加载
            </button>
          </div>
        ) : (
          <>
            {records.map((log) => (
              <button
                type="button"
                key={log.id}
                className="btn btn-ghost"
                style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}
                aria-pressed={id === log.id}
                onClick={() => setChosen(log.id)}
              >
                <span className="chip chip-info">
                  {log.processing_type === 'RULE'
                    ? '规则处理'
                    : log.processing_type === 'AI'
                      ? 'AI 脱敏'
                      : log.processing_type === 'MANUAL'
                        ? '人工修改'
                        : '原文快照'}
                </span>
                <ProjectState status={log.status} />
                <span className="mono">{fmtDateTime(log.created_at)}</span>
                <span>
                  {log.provider && landingAiProviderLabel(log.provider)} {log.model}
                </span>
                {log.is_current && <span className="chip">当前采用版本</span>}
              </button>
            ))}
            <Pagination
              total={history.data?.total ?? 0}
              page={page}
              pageSize={20}
              onChange={(next) => {
                setPage(next);
                setChosen(null);
              }}
            />
            {!records.length && (
              <p>
                {processingType && history.data?.total
                  ? '当前页没有此类正文版本，可查看其他页；审核结果不属于正文版本。'
                  : '暂无对应正文处理记录'}
              </p>
            )}
          </>
        )}
        {detail.isError ? (
          <p role="alert">{detail.error.message}</p>
        ) : id && detail.isPending ? (
          <p>加载实际正文 Diff…</p>
        ) : (
          detail.data && (
            <>
              {detail.data.log.error && (
                <p role="alert" style={{ color: 'var(--neg)' }}>
                  {detail.data.log.error}
                </p>
              )}
              {processingType === 'AI' &&
                detail.data.log.id === preferredVersionId &&
                ['RULE', 'SOURCE'].includes(detail.data.log.processing_type) && (
                  <p>
                    本次脱敏的最终结果为原文或词库规则版本；下方展示该步骤的实际修改，AI
                    改写请查看父版本。
                  </p>
                )}
              <div className="seg">
                <button
                  type="button"
                  className={mode === 'split' ? 'on' : ''}
                  onClick={() => setMode('split')}
                >
                  左右对照
                </button>
                <button
                  type="button"
                  className={mode === 'unified' ? 'on' : ''}
                  onClick={() => setMode('unified')}
                >
                  统一 Diff
                </button>
              </div>
              {detail.data.diff_available ? (
                detail.data.changes.length ? (
                  <div dir="auto">
                    <LandingTextDiff
                      changes={detail.data.changes}
                      mode={mode}
                      labels={{
                        before:
                          detail.data.log.processing_type === 'RULE'
                            ? '规则处理前'
                            : detail.data.log.processing_type === 'AI'
                              ? 'AI 脱敏前'
                              : '修改前',
                        after:
                          detail.data.log.processing_type === 'RULE'
                            ? '规则处理后'
                            : detail.data.log.processing_type === 'AI'
                              ? 'AI 脱敏后'
                              : '修改后',
                        description: '正文版本修改前后 Diff',
                      }}
                    />
                  </div>
                ) : (
                  <p>此版本没有正文修改</p>
                )
              ) : (
                <p>无法可靠还原此历史版本的 Diff</p>
              )}
              {detail.data.log.review_result && (
                <div>
                  <strong>此正文版本附带的审核发现项</strong>
                  {detail.data.log.review_result.illegal_words.map((item, index) => (
                    <p key={`word-${index}`}>
                      {item.word} · {item.illegal_type}
                    </p>
                  ))}
                  {detail.data.log.review_result.illegal_phrases.map((item, index) => (
                    <p key={`phrase-${index}`}>
                      {item.phrase} · {item.illegal_type}
                    </p>
                  ))}
                </div>
              )}
            </>
          )
        )}
      </div>
    </Drawer>
  );
}
